import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { User } from '../../features/users/models/user.model';
import { LoginResponse } from '../models/auth.model';
import { Role } from '../../features/users/models/role.enum';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user = signal<User | null>(this.loadUserFromStorage());
  readonly user = this._user;

  readonly isLoggedIn = signal(!!this._user());

  private readonly authUrl = `${environment.apiBaseUrl}/auth`;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  /**
   * LOGIN
   * El backend:
   * - valida credenciales
   * - verifica active = true
   * - verifica deleted = false
   * - retorna token + user (sin password)
   */
  login(email: string, password: string) {
    return this.http.post<LoginResponse>(`${this.authUrl}/login`, {
      email,
      password,
    });
  }

  /**
   * Obtener usuario autenticado
   * - Endpoint: GET /auth/me
   * - Requiere JWT válido
   * - Retorna usuario actual (sin password)
   */
  me() {
    return this.http.get<User>(`${this.authUrl}/me`);
  }

  /**
   * El backend debe:
   * - invalidar token
   * - limpiar sesión
   */
  logout() {
    this.clearSession();
    this.router.navigateByUrl('/login');
  }

  hasRole(allowed: Role[] | Role): boolean {
    const user = this._user();
    if (!user) return false;

    const roles = Array.isArray(allowed) ? allowed : [allowed];
    return roles.includes(user.role);
  }

  /**
   * Cambio de contraseña
   * El backend:
   * - hashea nueva contraseña
   * - marca mustChangePassword = false
   */
  changePassword(newPassword: string) {
    return this.http
      .post<User>(`${this.authUrl}/change-password`, {
        password: newPassword,
      })
      .pipe(
        tap((updatedUser) => {
          // ACTUALIZAR SESIÓN
          this._user.set(updatedUser);
          localStorage.setItem('kpi_user', JSON.stringify(updatedUser));
        }),
      );
  }

  /**
   * Forgot password
   * El backend:
   * - genera token temporal
   * - envía correo
   * - nunca revela si el email existe
   */
  forgotPassword(email: string) {
    return this.http.post(`${this.authUrl}/forgot-password`, { email });
  }

  /**
   * Sesión local (FRONT ONLY)
   */
  setSession(res: LoginResponse) {
    this._user.set(res.user as User);
    this.isLoggedIn.set(true);
    localStorage.setItem('kpi_token', res.token);
    localStorage.setItem('kpi_user', JSON.stringify(res.user));
  }

  restoreUser(user: User) {
    this._user.set(user);
    this.isLoggedIn.set(true);
    localStorage.setItem('kpi_user', JSON.stringify(user));
  }

  clearSession() {
    this._user.set(null);
    this.isLoggedIn.set(false);
    localStorage.removeItem('kpi_token');
    localStorage.removeItem('kpi_user');
  }

  private loadUserFromStorage(): User | null {
    try {
      const s = localStorage.getItem('kpi_user');
      return s ? (JSON.parse(s) as User) : null;
    } catch {
      return null;
    }
  }

  /**
   * Reset password con token
   * - No requiere JWT
   * - Usa token temporal
   */
  resetPassword(token: string, newPassword: string) {
    return this.http.post(`${this.authUrl}/reset-password`, {
      token,
      password: newPassword,
    });
  }
}
